import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import Swal from 'sweetalert2';

const TYPE_LEAVE = 'leave';
const TYPE_UNAVAILABILITY = 'unavailability';

const formatDate = (value) => {
    if (!value) return 'N/A';
    const datePart = String(value).split('T')[0];
    const [y, m, d] = datePart.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10) + 1);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatTime = (time) => {
    if (!time) return '—';
    const parts = String(time).split(':');
    if (parts.length < 2) return time;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1] ?? '00';
    const suffix = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
};

const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
};

const TypeBadge = ({ type }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
        type === TYPE_LEAVE
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-rose-50 text-rose-700 border-rose-200'
    }`}>
        {type === TYPE_LEAVE ? 'Leave' : 'Unavailability'}
    </span>
);

const emptyEdit = { type: TYPE_LEAVE, reason: '', date: '', startTime: '', endTime: '' };

export default function StaffUnavailability() {
    const [records, setRecords] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Status toggle: 'active' = current & future (default), 'past' = elapsed
    const [statusFilter, setStatusFilter] = useState('active');

    // Filters
    const [filterStaff, setFilterStaff] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Edit modal
    const [editRecord, setEditRecord] = useState(null);
    const [editForm, setEditForm] = useState(emptyEdit);
    const [submitting, setSubmitting] = useState(false);
    const [imageErrors, setImageErrors] = useState({});

    const handleImageError = (id) => setImageErrors((prev) => ({ ...prev, [id]: true }));

    useEffect(() => {
        fetchRecords();
    }, []);

    const buildQuery = () => {
        const params = new URLSearchParams();
        if (filterStaff) params.set('staffId', filterStaff.value);
        if (filterType !== 'all') params.set('type', filterType);
        if (filterDateFrom) params.set('dateFrom', filterDateFrom);
        if (filterDateTo) params.set('dateTo', filterDateTo);
        return params.toString();
    };

    const fetchRecords = async (overrideQuery) => {
        setLoading(true);
        try {
            const qs = overrideQuery !== undefined ? overrideQuery : '';
            const res = await axiosInstance.get(`/admin/staff-unavailability${qs ? `?${qs}` : ''}`);
            setRecords(res.data?.records || []);
            if (res.data?.staff) setStaffList(res.data.staff);
        } catch (e) {
            Swal.fire('Error', e?.response?.data?.message || 'Failed to load records', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => fetchRecords(buildQuery());

    const handleReset = () => {
        setFilterStaff(null);
        setFilterType('all');
        setFilterDateFrom('');
        setFilterDateTo('');
        fetchRecords('');
    };

    // --- Edit ---
    const openEdit = (record) => {
        setEditRecord(record);
        setEditForm({
            type: record.type,
            reason: record.reason,
            date: record.date || '',
            startTime: record.startTime || '',
            endTime: record.endTime || '',
        });
    };

    const closeEdit = () => { setEditRecord(null); setEditForm(emptyEdit); };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editRecord) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.put(`/admin/staff-unavailability/${editRecord.id}`, {
                type: editForm.type,
                reason: editForm.reason,
                date: editForm.date,
                startTime: editForm.type === TYPE_UNAVAILABILITY ? editForm.startTime : null,
                endTime: editForm.type === TYPE_UNAVAILABILITY ? editForm.endTime : null,
            });
            setRecords((prev) =>
                prev.map((r) => (r.id === editRecord.id ? { ...r, ...res.data.record } : r))
            );
            Swal.fire({ icon: 'success', title: 'Updated', text: 'Record updated successfully.', timer: 1500, showConfirmButton: false });
            closeEdit();
        } catch (e) {
            Swal.fire('Error', e?.response?.data?.message || 'Failed to update record', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // --- Delete ---
    const handleDelete = async (record) => {
        const result = await Swal.fire({
            title: 'Delete Record?',
            html: `<div class="text-sm text-gray-600">
                <p>Staff: <strong>${record.staffName}</strong></p>
                <p>Date: <strong>${formatDate(record.date)}</strong></p>
                <p>Type: <strong>${record.type === TYPE_LEAVE ? 'Leave' : 'Unavailability'}</strong></p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;
        try {
            await axiosInstance.delete(`/admin/staff-unavailability/${record.id}`);
            setRecords((prev) => prev.filter((r) => r.id !== record.id));
            Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
        } catch (e) {
            Swal.fire('Error', e?.response?.data?.message || 'Failed to delete', 'error');
        }
    };

    const today = new Date().toISOString().split('T')[0];

    const displayedRecords = useMemo(() => {
        return records
            .filter((r) => {
                const recDate = String(r.date || '').split('T')[0];
                return statusFilter === 'active' ? recDate >= today : recDate < today;
            })
            .sort((a, b) => {
                const dateA = String(a.date || '').split('T')[0];
                const dateB = String(b.date || '').split('T')[0];
                return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
            });
    }, [records, statusFilter, today]);

    const stats = useMemo(() => ({
        total: displayedRecords.length,
        leaves: displayedRecords.filter((r) => r.type === TYPE_LEAVE).length,
        unavailabilities: displayedRecords.filter((r) => r.type === TYPE_UNAVAILABILITY).length,
        uniqueStaff: new Set(displayedRecords.map((r) => r.staffId)).size,
    }), [displayedRecords]);

    return (
        <DashboardLayout>
            <div className="p-6 max-w-7xl mx-auto">

                {/* Page Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Staff Unavailability</h1>
                    <p className="text-sm text-gray-500 mt-1">View and manage all staff leave and unavailability records</p>
                </div>

                {/* Status Toggle */}
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-6 border border-gray-200">
                    <button
                        onClick={() => setStatusFilter('active')}
                        className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                            statusFilter === 'active'
                                ? 'bg-white text-indigo-700 shadow-sm border border-indigo-200'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <span className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${ statusFilter === 'active' ? 'bg-emerald-500' : 'bg-gray-400' }`} />
                            Active
                        </span>
                    </button>
                    <button
                        onClick={() => setStatusFilter('past')}
                        className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                            statusFilter === 'past'
                                ? 'bg-white text-slate-700 shadow-sm border border-slate-200'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <span className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${ statusFilter === 'past' ? 'bg-slate-400' : 'bg-gray-400' }`} />
                            Past
                        </span>
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { label: 'Total Records', value: stats.total, color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
                        { label: 'Leave', value: stats.leaves, color: 'bg-amber-50 border-amber-200 text-amber-700' },
                        { label: 'Unavailability', value: stats.unavailabilities, color: 'bg-rose-50 border-rose-200 text-rose-700' },
                        { label: 'Staff Members', value: stats.uniqueStaff, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                    ].map((s) => (
                        <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
                            <div className="text-2xl font-bold">{s.value}</div>
                            <div className="text-xs font-medium mt-0.5 opacity-80">{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Staff Member</label>
                            <Select
                                options={staffList.map((s) => ({ value: s.staffId, label: s.name }))}
                                value={filterStaff}
                                onChange={(opt) => setFilterStaff(opt)}
                                isClearable
                                placeholder="All Staff"
                                classNamePrefix="rs"
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        borderRadius: '0.5rem',
                                        borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
                                        boxShadow: state.isFocused ? '0 0 0 2px #a5b4fc' : 'none',
                                        fontSize: '0.875rem',
                                        minHeight: '38px',
                                        '&:hover': { borderColor: '#818cf8' },
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '0.875rem',
                                        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
                                        color: state.isSelected ? 'white' : '#111827',
                                    }),
                                    clearIndicator: (base) => ({ ...base, color: '#9ca3af', '&:hover': { color: '#6b7280' } }),
                                    menu: (base) => ({ ...base, borderRadius: '0.5rem', zIndex: 50 }),
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                                <option value="all">All Types</option>
                                <option value={TYPE_LEAVE}>Leave</option>
                                <option value={TYPE_UNAVAILABILITY}>Unavailability</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
                            <input
                                type="date"
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
                            <input
                                type="date"
                                value={filterDateTo}
                                onChange={(e) => setFilterDateTo(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                        >
                            Search
                        </button>
                        <button
                            onClick={handleReset}
                            className="px-4 py-2 text-sm bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded-lg font-medium transition-colors"
                        >
                            Reset
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-20 text-gray-400">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
                                <p className="text-sm">Loading records...</p>
                            </div>
                        </div>
                    ) : displayedRecords.length === 0 ? (
                        <div className="text-center py-20 text-gray-400">
                            <div className="text-4xl mb-3">📋</div>
                            <p className="font-medium">
                                {statusFilter === 'active' ? 'No active records found' : 'No past records found'}
                            </p>
                            <p className="text-sm mt-1">Try adjusting the filters or switching the status tab</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Staff Member</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Type</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Date</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Time</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Reason</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {displayedRecords.map((record) => {
                                        const showImg = record.imagePath && !imageErrors[record.id];
                                        const avatarSrc = showImg
                                            ? getImageURL(record.imagePath.startsWith('/') ? record.imagePath : `/${record.imagePath}`)
                                            : null;
                                        return (
                                            <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        {showImg ? (
                                                            <img
                                                                src={avatarSrc}
                                                                alt={record.staffName}
                                                                className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-200"
                                                                onError={() => handleImageError(record.id)}
                                                            />
                                                        ) : (
                                                            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0 border border-indigo-200">
                                                                {getInitials(record.staffName)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="font-medium text-gray-800 whitespace-nowrap">{record.staffName}</div>
                                                            {record.staffType && (
                                                                <div className="text-xs text-gray-400">{record.staffType}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3"><TypeBadge type={record.type} /></td>
                                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(record.date)}</td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                    {record.type === TYPE_UNAVAILABILITY
                                                        ? `${formatTime(record.startTime)} – ${formatTime(record.endTime)}`
                                                        : <span className="text-gray-400 text-xs">Full day</span>}
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 max-w-xs">
                                                    <p className="truncate" title={record.reason}>{record.reason}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openEdit(record)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(record)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {editRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6">
                        {/* Modal Header */}
                        <div className="flex items-start justify-between mb-5">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Edit Record</h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {editRecord.staffName} &mdash; {formatDate(editRecord.date)}
                                </p>
                            </div>
                            <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                                <select
                                    name="type"
                                    value={editForm.type}
                                    onChange={handleEditChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value={TYPE_LEAVE}>Leave (Full Day)</option>
                                    <option value={TYPE_UNAVAILABILITY}>Unavailability (Partial)</option>
                                </select>
                            </div>

                            {/* Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="date"
                                    value={editForm.date}
                                    onChange={handleEditChange}
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>

                            {/* Start / End time */}
                            {editForm.type === TYPE_UNAVAILABILITY && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Time <span className="text-red-500">*</span></label>
                                        <input
                                            type="time"
                                            name="startTime"
                                            value={editForm.startTime}
                                            onChange={handleEditChange}
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">End Time <span className="text-red-500">*</span></label>
                                        <input
                                            type="time"
                                            name="endTime"
                                            value={editForm.endTime}
                                            onChange={handleEditChange}
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Reason */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
                                <textarea
                                    name="reason"
                                    value={editForm.reason}
                                    onChange={handleEditChange}
                                    rows={3}
                                    required
                                    placeholder="Enter reason..."
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={closeEdit}
                                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-5 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-2"
                                >
                                    {submitting && <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                                    {submitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
